/**
 * Workplace Details Form Component
 * Comprehensive form for managing all workplace settings including business info, formats, and logo
 */

import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Loader2, Upload, X, Palette } from 'lucide-react';
import { useWorkplaceDetails, useUpdateWorkplaceDetails } from '../../hooks/business/useWorkplaceDetails';
import { LogoUploadComponent } from './LogoUploadComponent';
import { ColorPickerComponent } from './ColorPickerComponent';
import { CountrySelectComponent } from './CountrySelectComponent';
import { DATE_FORMAT_OPTIONS, TIME_FORMAT_OPTIONS } from '../../utils/formatters';
import type { WorkplaceDetailsUpdate } from '../../services/business/types';

// Form validation schema
const workplaceDetailsSchema = z.object({
  businessName: z.string().min(2, 'Business name must be at least 2 characters').optional(),
  timeFormat: z.enum(['12h', '24h']).optional(),
  dateFormat: z.enum(['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']).optional(),
  countryCode: z.union([z.string().length(2), z.literal('')]).transform(val => val || undefined),
  currencyCode: z.union([z.string().length(3), z.literal('')]).transform(val => val || undefined),
  themeColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color').optional(),
});

type FormData = z.infer<typeof workplaceDetailsSchema>;

interface WorkplaceDetailsFormProps {
  onSuccess?: () => void;
  className?: string;
}

export const WorkplaceDetailsForm: React.FC<WorkplaceDetailsFormProps> = ({
  onSuccess,
  className,
}) => {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { data: workplaceDetails, isLoading: isLoadingDetails } = useWorkplaceDetails();
  const updateMutation = useUpdateWorkplaceDetails();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(workplaceDetailsSchema),
    defaultValues: {
      businessName: workplaceDetails?.business_name || '',
      timeFormat: workplaceDetails?.time_format || '12h',
      dateFormat: workplaceDetails?.date_format || 'DD/MM/YYYY',
      countryCode: workplaceDetails?.country_code || '',
      currencyCode: workplaceDetails?.currency_code || '',
      themeColor: workplaceDetails?.theme_color || '#3b82f6',
    },
  });

  // Update form only on initial load, not during user edits
  React.useEffect(() => {
    if (workplaceDetails && !updateMutation.isPending && !isDirty) {
      reset({
        businessName: workplaceDetails.business_name || '',
        timeFormat: workplaceDetails.time_format || '12h',
        dateFormat: workplaceDetails.date_format || 'DD/MM/YYYY',
        countryCode: workplaceDetails.country_code || '',
        currencyCode: workplaceDetails.currency_code || '',
        themeColor: workplaceDetails.theme_color || '#3b82f6',
      });
    }
  }, [workplaceDetails, reset, updateMutation.isPending, isDirty]);

  const watchedValues = watch();

  // Cleanup preview URL on unmount to prevent memory leaks
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleLogoSelect = useCallback((file: File) => {
    // Cleanup previous URL before setting new one
    setPreviewUrl(prev => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return URL.createObjectURL(file);
    });
    setLogoFile(file);
  }, []);

  const handleLogoRemove = useCallback(() => {
    setPreviewUrl(prev => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return null;
    });
    setLogoFile(null);
  }, []);

  const handleCountryChange = useCallback((countryCode: string, currencyCode: string) => {
    setValue('countryCode', countryCode, { shouldDirty: true });
    setValue('currencyCode', currencyCode, { shouldDirty: true });
  }, [setValue]);

  const handleColorChange = useCallback((color: string) => {
    setValue('themeColor', color, { shouldDirty: true });
  }, [setValue]);

  const onSubmit = async (data: FormData) => {
    try {
      const updates: WorkplaceDetailsUpdate = {
        businessName: data.businessName,
        timeFormat: data.timeFormat,
        dateFormat: data.dateFormat,
        countryCode: data.countryCode,
        currencyCode: data.currencyCode,
        themeColor: data.themeColor,
        logoFile: logoFile || undefined,
      };

      await updateMutation.mutateAsync(updates);
      
      // Reset logo file state after successful update
      setLogoFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      
      onSuccess?.();
    } catch (error) {
      console.error('Error updating workplace details:', error);
    }
  };

  const isLoading = isLoadingDetails || updateMutation.isPending;
  const hasChanges = isDirty || logoFile !== null;

  if (isLoadingDetails) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">Loading workplace details...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Workplace Details</CardTitle>
        <CardDescription>
          Configure your business information, display formats, and branding
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Business Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Business Information</h3>
            
            <div className="space-y-2">
              <Label htmlFor="businessName">Business Name</Label>
              <Input
                id="businessName"
                {...register('businessName')}
                placeholder="Enter your business name"
                disabled={isLoading}
              />
              {errors.businessName && (
                <p className="text-sm text-red-600">{errors.businessName.message}</p>
              )}
            </div>

            <CountrySelectComponent
              value={watchedValues.countryCode}
              onCountryChange={handleCountryChange}
              disabled={isLoading}
              error={errors.countryCode?.message}
            />
          </div>

          <Separator />

          {/* Display Formats Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Display Formats</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="timeFormat">Time Format</Label>
                <Select
                  value={watchedValues.timeFormat}
                  onValueChange={(value) => setValue('timeFormat', value as '12h' | '24h', { shouldDirty: true })}
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select time format" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_FORMAT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.timeFormat && (
                  <p className="text-sm text-red-600">{errors.timeFormat.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="dateFormat">Date Format</Label>
                <Select
                  value={watchedValues.dateFormat}
                  onValueChange={(value) => setValue('dateFormat', value as any, { shouldDirty: true })}
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select date format" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_FORMAT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.dateFormat && (
                  <p className="text-sm text-red-600">{errors.dateFormat.message}</p>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Branding Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Branding</h3>
            
            <div className="space-y-4">
              <ColorPickerComponent
                value={watchedValues.themeColor || '#3b82f6'}
                onChange={handleColorChange}
                disabled={isLoading}
                error={errors.themeColor?.message}
              />

              <LogoUploadComponent
                currentLogoUrl={workplaceDetails?.logo_url}
                previewUrl={previewUrl}
                onLogoSelect={handleLogoSelect}
                onLogoRemove={handleLogoRemove}
                disabled={isLoading}
              />
            </div>
          </div>

          <Separator />

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                handleLogoRemove();
              }}
              disabled={isLoading || !hasChanges}
            >
              Reset
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !hasChanges}
            >
              <Loader2 
                className={`mr-2 h-4 w-4 ${updateMutation.isPending ? 'animate-spin' : 'hidden'}`} 
              />
              Save Changes
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default WorkplaceDetailsForm;
